import React, { useMemo, useEffect, useState } from 'react';
import WorkflowTimelineBottomBar from '../../components/common/WorkflowTimelineBottomBar';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import moment from 'moment';
import { Box, Button, Chip, Paper, Typography, Grid, Stack, Skeleton, Collapse, IconButton, Tooltip, Avatar, Badge, CircularProgress, Divider, Tabs, Tab } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MonitorHeartRoundedIcon from '@mui/icons-material/MonitorHeartRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PersonRoundedIcon from '@mui/icons-material/PersonRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import BoltIcon from '@mui/icons-material/Bolt';
import StreamRoundedIcon from '@mui/icons-material/StreamRounded';
import PublicRoundedIcon from '@mui/icons-material/PublicRounded';
import PsychologyAltRoundedIcon from '@mui/icons-material/PsychologyAltRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import useAPICall from '../../hooks/useAPICall';
import { API_CONFIG } from '../../config/api.config';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';

const getWorkflowStepPresentation = (step = {}) => {
  const normalizedState = String(step?.state || step?.status || '').toLowerCase();
  const normalizedTitle = String(step?.title || step?.step || step?.stepName || '').toLowerCase();

  if (
    ['completed', 'complete', 'done', 'approved', 'verified', 'success'].includes(normalizedState)
  ) {
    return {
      state: 'completed',
      chipLabel: 'Completed',
      chipColor: '#16a34a',
      prefix: 'Done',
      showSpinner: false,
    };
  }

  if (
    [
      'running',
      'active',
      'in_progress',
      'in progress',
      'pending_review',
      'waiting',
      'waiting_for_review',
      'human_review_required',
      'review_required',
    ].includes(normalizedState)
  ) {
    const isWaiting =
      normalizedTitle.includes('waiting') ||
      normalizedTitle.includes('review') ||
      normalizedState.includes('review');

    return {
      state: 'running',
      chipLabel: isWaiting ? 'Awaiting Review' : 'In Progress',
      chipColor: '#f97316',
      prefix: isWaiting ? 'Waiting' : 'Live',
      showSpinner: true,
    };
  }

  return {
    state: 'idle',
    chipLabel: 'Queued',
    chipColor: '#64748b',
    prefix: '',
    showSpinner: false,
  };
};

const isCompletedWorkflowState = (state) =>
  ['completed', 'complete', 'done', 'approved', 'verified', 'success'].includes(
    String(state || '').toLowerCase()
  );


const formatTimestamp = (value) => (value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-');

const formatDisplayDate = (value) => (value ? moment(value).format('MMM D, YYYY hh:mm A') : '-');

const displayValue = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).join('-') || '-';
  if (typeof value === 'boolean') return String(value);
  if (value === undefined || value === null || value === '') return '-';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const firstPresentValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== '') ?? '-';

const formatTableData = (state, ref, otherData = []) => {
  if (!state) return [];
  if (Array.isArray(state)) return state;
  if (typeof state !== 'object') return [];

  const rows = [];
  Object.keys(state).forEach((key) => {
    if (ref === 'isn') {
      Object.keys(state[key] || {}).forEach((nestedKey) => {
        rows.push({ ...(state[key]?.[nestedKey] || {}), Id: nestedKey, isnCode: key });
      });
    } else if (ref === 'sln') {
      rows.push({ ...(state[key] || {}), slnAuthNbr: key });
    } else if (ref === 'addr') {
      const addr = { ...(state[key] || {}), addrId: key };
      if (Array.isArray(otherData) && otherData.length > 0) {
        addr.overriddenAddressFields = [];
        otherData.forEach((item) => {
          if (typeof item !== 'string') return;
          if (item.length === 41 && addr.addrId === item.substring(5)) {
            addr.overriddenAddress = item.substring(5);
          } else if (item.length > 41 && addr.addrId === item.substring(5, 41)) {
            addr.overriddenAddressFields.push(item.substring(42));
          }
        });
      }
      rows.push(addr);
    } else if (ref === 'spc' || ref === 'subspec') {
      rows.push({ ...(state[key] || {}), rank: key });
    } else if (ref === 'src') {
      rows.push({ ...(state[key] || {}), srname: key });
    }
  });

  return rows;
};

const getTabValue = (row, keys = []) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '-';
};

const isMasterPrimaryAddress = (row) =>
  row?.masterPrimary === true || String(row?.masterPrimary).toLowerCase() === 'true';

const sortMasterPrimaryFirst = (rows) => {
  if (!Array.isArray(rows)) return [];
  return [...rows].sort((a, b) => {
    const primarySort = Number(isMasterPrimaryAddress(b)) - Number(isMasterPrimaryAddress(a));
    if (primarySort !== 0) return primarySort;

    const leftRank = Number(a?.rank ?? Number.NEGATIVE_INFINITY);
    const rightRank = Number(b?.rank ?? Number.NEGATIVE_INFINITY);
    return rightRank - leftRank;
  });
};

const HCP_SOURCE_COLUMNS = [
  { label: 'Source Name', keys: ['source', 'srname'] },
  { label: 'External Id', keys: ['externalId'] },
  { label: 'Prof Id', keys: ['srcProfId'] },
  { label: 'Merge Status', keys: ['mergeStatus'] },
  { label: 'First Name', keys: ['firstName'] },
  { label: 'Middle Name', keys: ['middleName'] },
  { label: 'Last Name', keys: ['lastName'] },
  { label: 'Suffix', keys: ['suffix'] },
  { label: 'Birth Date', keys: ['birthDate'] },
  { label: 'Gender', keys: ['gender'] },
  { label: 'Degree', keys: ['degree'] },
  { label: 'Graduation Year', keys: ['graduationYear'] },
  { label: 'Medical School', keys: ['medSchoolDesc'] },
  { label: 'Phone', keys: ['cellPhone'] },
  { label: 'Updated Date', keys: ['updatedDate'] },
];

const HCP_ADDRESS_COLUMNS = [
  { label: 'Source', keys: ['source'] },
  { label: 'Rank', keys: ['rank'] },
  { label: 'Address Line 1', keys: ['addrLn1'] },
  { label: 'City', keys: ['cityNm'] },
  { label: 'State', keys: ['stCd'] },
  { label: 'Zip', keys: ['zipCd'] },
  { label: 'Master Primary', keys: ['masterPrimary'] },
  { label: 'External Id', keys: ['externalId'] },
  { label: 'Veeva Primary', keys: ['primary'] },
  { label: 'Client Preferred', keys: ['clientPreferred', 'primary'] },
  { label: 'Ok Best', keys: ['okBestAddr'] },
  { label: 'Address Line 2', keys: ['addrLn2'] },
  { label: 'Address Id', keys: ['addrId'] },
  { label: 'Status', keys: ['statusLabel', 'status'] },
  { label: 'Has State Sanction', keys: ['hasStateSanction'] },
  { label: 'Is Sampleable', keys: ['isSampleable'] },
  { label: 'Setting', keys: ['setting'] },
  { label: 'Category', keys: ['category'] },
  { label: 'Business Name', keys: ['busNm'] },
  { label: 'Rep Managed', keys: ['repManaged'] },
];

const HCP_SPECIALTY_COLUMNS = [
  { label: 'Rank', keys: ['rank'] },
  { label: 'Specialty Description', keys: ['spclDesc', 'specialty', 'speciality', 'value'] },
  { label: 'Specialty Code', keys: ['spclCode'] },
  { label: 'Specialty Group', keys: ['clientSpclGroup'] },
  { label: 'Professional Description', keys: ['prfsnDesc'] },
];

const HCP_SLN_COLUMNS = [
  { label: 'Auth Number', keys: ['slnAuthNbr'] },
  { label: 'Status', keys: ['authnStatDesc', 'status'] },
  { label: 'State Code', keys: ['stCd'] },
  { label: 'Board ID', keys: ['stBrdId'] },
  { label: 'Board Name', keys: ['stBrdNm'] },
  { label: 'Type Name', keys: ['stLicTypNm'] },
  { label: 'Privilege', keys: ['stLicPrivlNm'] },
  { label: 'Effective Date', keys: ['authnEffDt'] },
  { label: 'Expiration Date', keys: ['authnTrmtnDt'] },
];

const HCP_ISN_COLUMNS = [
  { label: 'Code', keys: ['isnCode'] },
  { label: 'Id', keys: ['Id'] },
  { label: 'Status', keys: ['status'] },
  { label: 'Effective Date', keys: ['effDate'] },
  { label: 'Termination Date', keys: ['termnDate'] },
  { label: 'Deactivation Reason', keys: ['deactvtnRsn'] },
];

const parseWorkflowMessage = (message) => {
  if (!message || typeof message !== 'string') return null;
  try {
    return JSON.parse(message);
  } catch {
    return null;
  }
};

const getFieldFromValue = (fieldNames, value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const parsed = parseWorkflowMessage(value);
    if (parsed) {
      return getFieldFromValue(fieldNames, parsed);
    }
    return value;
  }
  if (typeof value === 'object') {
    for (const fieldName of fieldNames) {
      const extracted =
        value?.[fieldName] ??
        value?.data?.[fieldName] ??
        value?.result?.[fieldName] ??
        value?.payload?.[fieldName] ??
        value?.message?.[fieldName];
      if (extracted !== undefined && extracted !== null && extracted !== '') {
        return extracted;
      }
    }
  }
  return '';
};

const getWorkflowFieldText = (fieldNames, ...values) => {
  for (const value of values) {
    const fieldValue = getFieldFromValue(fieldNames, value);
    if (fieldValue) return fieldValue;
  }
  return '';
};

const getWorkflowQueryText = (...values) => getWorkflowFieldText(['query', 'sqlQuery', 'searchQuery', 'search_query'], ...values);

const formatTimelineKey = (key = '') =>
  String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase());

const getConfidenceMeta = (score) => {
  const numericScore = Number.parseFloat(score);
  if (!Number.isFinite(numericScore)) return null;
  const value = Math.max(0, Math.min(100, numericScore));

  if (value >= 90) return { value, label: 'Auto Approve', color: '#16a34a', bg: '#dcfce7' };
  if (value >= 75) return { value, label: 'Approve Recommended', color: '#16a34a', bg: '#dcfce7' };
  if (value >= 60) return { value, label: 'Human Review Required', color: '#d97706', bg: '#fef3c7' };
  if (value >= 40) return { value, label: 'Low Confidence', color: '#ea580c', bg: '#ffedd5' };
  return { value, label: 'Reject Recommended', color: '#dc2626', bg: '#fee2e2' };
};

const buildRecommendationBullets = (workflow = {}, recordType = 'Address') => {
  const oldLocation = [workflow.old_cityNm, workflow.old_stcd, workflow.old_zip].filter(Boolean).join(', ');
  const newLocation = [workflow.new_cityNm, workflow.new_stcd, workflow.new_zip].filter(Boolean).join(', ');
  const oldTrx = workflow.old_trx ?? workflow.oldTrx;
  const newTrx = workflow.new_trx ?? workflow.newTrx;

  return [
    oldLocation && newLocation
      ? `Geographic: primary location changes from ${oldLocation} to ${newLocation}.`
      : 'Geographic: location change requires validation against source records.',
    oldTrx !== undefined || newTrx !== undefined
      ? `Prescription volume: old TRX for old zip ${oldTrx ?? '-'} vs new TRX for new zip ${newTrx ?? '-'}.`
      : 'Prescription volume: data is not available for meaningful automated comparison.',
    recordType === 'Address'
      ? 'Insufficient automated evidence to approve the address change.'
      : 'Recommendation confidence is based on workflow evidence and LLM scoring.',
    'Human review is required to make the final decision.',
  ];
};

const getZipPart = (zip, index = 0) => {
  if (Array.isArray(zip)) return zip[index] || '';
  if (typeof zip === 'string' && zip.includes('-')) return zip.split('-')[index] || '';
  return index === 0 ? zip : '';
};

const getTimelineVisuals = (item, index) => {
  const key = String(item?.status || item?.title || item?.name || item?.eventName || item?.stepName || '').toLowerCase();

  if (key.includes('agent started review') || key.includes('auto-dcr requested') || key.includes('created')) {
    return { accent: '#22c55e', icon: BoltIcon };
  }
  if (key.includes('retrieved') || key.includes('loading') || key.includes('extracting')) {
    return { accent: '#38bdf8', icon: StreamRoundedIcon };
  }
  if (key.includes('searching') || key.includes('comparing') || key.includes('external')) {
    return { accent: '#38bdf8', icon: PublicRoundedIcon };
  }
  if (key.includes('validating') || key.includes('checking') || key.includes('analyzing')  || key.includes('running')) {
    return { accent: '#a78bfa', icon: PsychologyAltRoundedIcon };
  }
  if (key.includes('evidence verified') || key.includes('recommendation') || key.includes('ready')) {
    return { accent: '#f59e0b', icon: ShieldRoundedIcon };
  }
  if (key.includes('waiting') || key.includes('human review')) {
    return { accent: '#f97316', icon: HourglassTopRoundedIcon };
  }
  if (key.includes('automatic approval completed')) {
    return { accent: '#16a34a', icon: CheckCircleRoundedIcon };
  }

  const fallbackVisuals = [
    { accent: '#38bdf8', icon: StreamRoundedIcon },
    { accent: '#22c55e', icon: BoltIcon },
    { accent: '#a78bfa', icon: PsychologyAltRoundedIcon },
    { accent: '#38bdf8', icon: PublicRoundedIcon },
    { accent: '#fb923c', icon: ShieldRoundedIcon },
  ];

  return fallbackVisuals[index] || fallbackVisuals[fallbackVisuals.length - 1];
};

const getRequestId = (row) => row?.request_id || row?.requestId || row?.id || '';

const normalizeOverallStatus = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();

  if (!normalized) return '';
  if (
    normalized === 'waiting_for_manual_review' ||
    normalized === 'waiting_for_review' ||
    normalized === 'pending_review' ||
    normalized === 'human_review_required' ||
    normalized === 'review_required' ||
    normalized === 'awaiting_review'
  ) {
    return 'AWAITING_REVIEW';
  }
  if (normalized === 'completed' || normalized === 'complete' || normalized === 'done') {
    return 'COMPLETED';
  }
  if (normalized === 'failed' || normalized === 'error' || normalized === 'rejected') {
    return 'FAILED';
  }
  return String(value).toUpperCase();
};

const extractTimelineItems = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.result)) return response.result;
  if (Array.isArray(response?.payload)) return response.payload;
  if (Array.isArray(response?.events)) return response.events;
  if (Array.isArray(response?.steps)) return response.steps;
  if (Array.isArray(response?.workflow)) return response.workflow;
  if (Array.isArray(response?.workflowSteps)) return response.workflowSteps;
  if (Array.isArray(response?.timeline)) return response.timeline;
  if (Array.isArray(response?.agentStatus)) return response.agentStatus;
  if (Array.isArray(response?.agentStatuses)) return response.agentStatuses;
  if (Array.isArray(response?.statusList)) return response.statusList;
  if (Array.isArray(response?.data?.result)) return response.data.result;
  if (Array.isArray(response?.data?.payload)) return response.data.payload;
  if (Array.isArray(response?.data?.events)) return response.data.events;
  if (Array.isArray(response?.data?.steps)) return response.data.steps;
  if (Array.isArray(response?.data?.workflow)) return response.data.workflow;
  if (Array.isArray(response?.data?.workflowSteps)) return response.data.workflowSteps;
  if (Array.isArray(response?.data?.timeline)) return response.data.timeline;
  if (Array.isArray(response?.data?.agentStatus)) return response.data.agentStatus;
  if (Array.isArray(response?.data?.agentStatuses)) return response.data.agentStatuses;
  if (Array.isArray(response?.data?.statusList)) return response.data.statusList;
  return [];
};

const cardStyle = {
  p: 2.5,
  borderRadius: '12px',
  border: '0.5px solid #e2e8f0',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: 'none',
  backgroundColor: '#fff',
};

const HistoryRequestDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { requestId: paramRequestId } = useParams();
  const [searchParams] = useSearchParams();

  const locationRequestRow = location.state?.requestData;
  const queryRequestId = searchParams.get('reqId') || searchParams.get('requestId');
  const effectiveRequestId = getRequestId(locationRequestRow) || paramRequestId || queryRequestId || '';
  const [loadedRequestRow, setLoadedRequestRow] = useState(null);
  
  const selectedRow = useMemo(() => {
    if (!loadedRequestRow) return locationRequestRow;
    if (!locationRequestRow) return loadedRequestRow;
    return { ...locationRequestRow, ...loadedRequestRow };
  }, [locationRequestRow, loadedRequestRow]);

  const displayStatus =
    selectedRow?.mdm_dcr_status || selectedRow?.status || selectedRow?.requestStatus || '-';

  const requestType = selectedRow?.request_type || selectedRow?.action || '-';

  const requestor = selectedRow?.owner_details ?? {};

  const account = selectedRow?.account_details ?? {};
  const selectedMdmIdRaw =
    account?.mdm_id ||
    account?.mdmId ||
    selectedRow?.mdm_id ||
    selectedRow?.mdmId ||
    selectedRow?.account_id ||
    selectedRow?.accountId ||
    '';
  const selectedMdmId =
    selectedMdmIdRaw && String(selectedMdmIdRaw).trim() !== '-' ? String(selectedMdmIdRaw) : '';

  const selectedFieldNames = useMemo(() => {
    const values = selectedRow?.field_name || selectedRow?.fieldName;
    if (!values) return [];
    return Array.isArray(values) ? values : [values];
  }, [selectedRow]);
  const [sourceRecordsTabIndex, setSourceRecordsTabIndex] = useState(1);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 5,
  });

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [sourceRecordsTabIndex]);

  const { loading: loadingStatus, data: statusDataResponse, execute: executeStatus } = useAPICall();
  const { loading: loadingTimeline, data: timelineDataResponse, execute: executeTimeline } = useAPICall();
  const { loading: loadingRequestDetails, execute: executeRequestDetails } = useAPICall();
  const {
    loading: hcpProfileLoading,
    data: hcpProfileData,
    execute: executeHcpProfile,
  } = useAPICall();
  const { loading: loadingCards, data: cardsDetailsResponse, execute: executeCardsDetails } = useAPICall();
  const cardsDetails = cardsDetailsResponse || null;

  
  useEffect(() => {
    const fetchStatusData = async () => {
      const requestId = effectiveRequestId;
      if (!requestId) return;

      try {
        // Always fetch request details, whether locationRequestRow exists or not
        // This ensures the API endpoint is always called
        const requestResponse = await executeRequestDetails(API_CONFIG.workflow.getClosedDcrByRequestId, null, {
          params: { reqId: requestId },
          showErrorToast: false,
          errorMessage: 'Unable to load request details.',
        });

        const requestPayload =
          requestResponse?.request ||
          requestResponse?.data?.request ||
          requestResponse?.data ||
          requestResponse?.result ||
          requestResponse ||
          null;

        if (requestPayload && typeof requestPayload === 'object') {
          setLoadedRequestRow(requestPayload);
        }

        executeStatus(API_CONFIG.inbox.getAgentWorkflowStatus, null, {
          params: { requestId },
          showErrorToast: false,
          errorMessage: 'Unable to fetch agentic workflow status.'
        }).catch((e) => console.log('executeStatus failed', e));

        executeTimeline(API_CONFIG.inbox.getAgentStatus, { request_id: requestId }, {
          showErrorToast: false,
          errorMessage: 'Unable to fetch workflow timeline.'
        }).catch((e) => console.log('executeTimeline failed', e));

        executeCardsDetails(API_CONFIG.dcr.getCardsDetails, { requestId }, {
          showErrorToast: false,
        }).catch((e) => console.log('executeCardsDetails failed', e));
      } catch (error) {
        console.log(error, "error");
      }
    };

    fetchStatusData();
  }, [effectiveRequestId, locationRequestRow, executeRequestDetails, executeStatus, executeTimeline, executeCardsDetails]);

  useEffect(() => {
    if (!selectedMdmId) {
      return;
    }

    executeHcpProfile(API_CONFIG.workflow.getHcpByMdmId, null, {
      params: { mdmId: selectedMdmId },
      showErrorToast: false,
      errorMessage: 'Unable to load HCP details.',
    }).catch(() => {
      // Keep request details visible even when profile lookup fails.
    });
  }, [executeHcpProfile, selectedMdmId]);

  const hcpProfileSource = useMemo(() => {
    const payload = hcpProfileData?.data || hcpProfileData || {};
    return payload?.accountDetails || payload;
  }, [hcpProfileData]);

  const sourceRecordTableData = useMemo(
    () => ({
      srcRecord: formatTableData(hcpProfileSource?.srcRecord, 'src'),
      addressDetails: sortMasterPrimaryFirst(
        formatTableData(
          hcpProfileSource?.addressDetailsMap ||
            hcpProfileSource?.accountAddressDetails ||
            hcpProfileSource?.allAddressDetails ||
            hcpProfileSource?.addressDetails,
          'addr',
          hcpProfileSource?.overriddenAddress
        )
      ),
      specialtyDetails: formatTableData(hcpProfileSource?.specialtyDetails, 'spc'),
      slnDetails: formatTableData(hcpProfileSource?.slnDetails, 'sln'),
      isn: formatTableData(hcpProfileSource?.isn || hcpProfileSource?.isnDetails, 'isn'),
    }),
    [hcpProfileSource]
  );

  const sourceRecordTabConfigs = useMemo(
    () => [
      {
        label: 'Source Records',
        columns: HCP_SOURCE_COLUMNS,
        rows: sourceRecordTableData.srcRecord,
        emptyMessage: 'No source records found',
        keyPrefix: 'history-src',
      },
      {
        label: 'Address',
        columns: HCP_ADDRESS_COLUMNS,
        rows: sourceRecordTableData.addressDetails,
        emptyMessage: 'No address records found',
        keyPrefix: 'history-addr',
      },
      {
        label: 'Specialty',
        columns: HCP_SPECIALTY_COLUMNS,
        rows: sourceRecordTableData.specialtyDetails,
        emptyMessage: 'No specialty records found',
        keyPrefix: 'history-spc',
      },
      {
        label: 'SLN',
        columns: HCP_SLN_COLUMNS,
        rows: sourceRecordTableData.slnDetails,
        emptyMessage: 'No SLN records found',
        keyPrefix: 'history-sln',
      },
      {
        label: 'ISN',
        columns: HCP_ISN_COLUMNS,
        rows: sourceRecordTableData.isn,
        emptyMessage: 'No ISN records found',
        keyPrefix: 'history-isn',
      },
    ],
    [sourceRecordTableData]
  );

  const activeSourceRecordTab = sourceRecordTabConfigs[sourceRecordsTabIndex] || sourceRecordTabConfigs[1];

  const hcpDetailItems = useMemo(() => {
    const primaryAddress = hcpProfileSource?.primaryAddress || {};
    const fullName = [
      hcpProfileSource?.firstName,
      hcpProfileSource?.middleName,
      hcpProfileSource?.lastName,
    ]
      .filter(Boolean)
      .join(' ');

    return [
      { label: 'Source', value: hcpProfileSource?.source || account?.source || '-' },
      { label: 'Gender', value: hcpProfileSource?.gender || '-' },
      { label: 'Birth Year', value: hcpProfileSource?.birthYear || hcpProfileSource?.birthDate || '-' },
      { label: 'Address Line 1', value: primaryAddress?.addrLn1 || hcpProfileSource?.addressLine || '-' },
      { label: 'Address Line 2', value: primaryAddress?.addrLn2 || hcpProfileSource?.addressLine2 || '-' },
      { label: 'City', value: primaryAddress?.cityNm || hcpProfileSource?.city || '-' },
      { label: 'State', value: primaryAddress?.stCd || hcpProfileSource?.state || '-' },
      { label: 'Zip', value: primaryAddress?.zipCd?.[0] || hcpProfileSource?.zip || '-' },
      {
        label: 'Primary Specialty',
        value: hcpProfileSource?.primarySpecialty || hcpProfileSource?.specialty || '-',
      },
      // { label: 'Full Name', value: fullName || account?.name || account?.account_name || '-' },
      // { label: 'First Name', value: hcpProfileSource?.firstName || '-' },
      // { label: 'Middle Name', value: hcpProfileSource?.middleName || '-' },
      // { label: 'Last Name', value: hcpProfileSource?.lastName || '-' },
      // { label: 'MDM ID', value: selectedMdmId || account?.mdm_id || account?.mdmId || '-' },
      // {
      //   label: 'Onekey Individual ID',
      //   value: hcpProfileSource?.okIndvId || hcpProfileSource?.onekeyId || account?.onekeyId || '-',
      // },
      {
        label: 'Primary Profession',
        value: hcpProfileSource?.primaryProfession || hcpProfileSource?.profession || '-',
      },
      // { label: 'NPI ID', value: hcpProfileSource?.npiId || hcpProfileSource?.npi || '-' },
      {
        label: 'Status',
        value:
          hcpProfileSource?.statusLabel ||
          hcpProfileSource?.status ||
          hcpProfileSource?.activeStatus ||
          '-',
      },
      {
        label: 'MDM Created Time',
        value: hcpProfileSource?.mdmCreatedDate || hcpProfileSource?.mdmCreatedTime || '-',
      },
      {
        label: 'MDM Updated Time',
        value: hcpProfileSource?.mdmUpdatedDate || hcpProfileSource?.mdmUpdatedTime || '-',
      },
    ];
  }, [account, hcpProfileSource, selectedMdmId]);

  const statusData = useMemo(() => {
    if (!statusDataResponse) return [];
    
    if (Array.isArray(statusDataResponse.agenticWorkflowStatuses) && statusDataResponse.agenticWorkflowStatuses.length > 0) {
      return statusDataResponse.agenticWorkflowStatuses;
    }
    
    if (Array.isArray(statusDataResponse.agentStatuses)) {
      const agenticStep = statusDataResponse.agentStatuses.find(step => step.step === 'agentic_workflow' || step.stepName === 'agentic_workflow');
      if (agenticStep && agenticStep.message) {
        const parsed = parseWorkflowMessage(agenticStep.description);
        return [parsed || agenticStep];
      }
    }
    
    return [];
  }, [statusDataResponse]);

  const workflowSteps = useMemo(() => {
    const rawSteps = extractTimelineItems(timelineDataResponse) || [];
    if (rawSteps.length === 0) return [];

    const workflowQuery = firstPresentValue(
      timelineDataResponse?.query,
      timelineDataResponse?.data?.query,
      timelineDataResponse?.result?.query,
      timelineDataResponse?.payload?.query,
      timelineDataResponse?.agenticWorkflowStatuses?.find((step) =>
        String(step?.step || step?.stepName || step?.name || step?.title || '').toLowerCase().includes('trx')
      )?.query,
      statusDataResponse?.agenticWorkflowStatuses?.[0]?.query,
      statusDataResponse?.agenticWorkflowStatuses?.find((step) =>
        String(step?.step || step?.stepName || step?.name || step?.title || '').toLowerCase().includes('trx')
      )?.query,
      statusDataResponse?.agenticWorkflowStatuses?.find((step) => step?.query)?.query,
      selectedRow?.query,
      selectedRow?.sqlQuery,
      selectedRow?.searchQuery,
      statusDataResponse?.query,
      statusDataResponse?.data?.query
    );

    return rawSteps.map((step) => {
      const stepKey = String(step.step || step.stepName || step.eventName || step.name || step.title || '').toLowerCase();
      if (
        stepKey.includes('prescription') ||
        stepKey.includes('trx') ||
        stepKey.includes('retrieve_trx_data') ||
        stepKey.includes('retrieving prescription') ||
        stepKey.includes('retrieving_prescription')
      ) {
        return {
          ...step,
          query: step.query || workflowQuery,
        };
      }
      return step;
    });
  }, [timelineDataResponse, selectedRow, statusDataResponse]);

  const requestId = effectiveRequestId;
  const agentSummary = statusData?.[0] || {};
  const primaryAddress = hcpProfileSource?.primaryAddress || {};
  const hcpName = [
    hcpProfileSource?.firstName,
    hcpProfileSource?.middleName,
    hcpProfileSource?.lastName,
  ].filter(Boolean).join(' ') || account?.name || account?.account_name || '-';
  const headerStatus = firstPresentValue(
    statusDataResponse?.detailedStatus,
    statusDataResponse?.agenticWorkflowStatuses?.[0]?.detailedStatus,
    timelineDataResponse?.detailedStatus,
    selectedRow?.detailedStatus,
    selectedRow?.mdm_dcr_status,
    selectedRow?.status,
    selectedRow?.requestStatus,
    statusDataResponse?.overallStatus,
    statusDataResponse?.status,
    timelineDataResponse?.overallStatus,
    timelineDataResponse?.status,
    displayStatus === '-' ? 'Awaiting Review' : displayStatus
  );
  const createdDateValue = selectedRow?.created_date || selectedRow?.created_at || selectedRow?.createdAt;
  const updatedDateValue =
    selectedRow?.last_modified_date || selectedRow?.modified_at || selectedRow?.updated_at;
  const requestDateDisplay = formatDisplayDate(createdDateValue);
  const updatedDateDisplay = formatDisplayDate(updatedDateValue);
  const requestTypeLabel = `${selectedRow?.record_type || selectedRow?.recordType || requestType || 'Address'} Change`;
  const isAddressReq = (selectedRow?.record_type || selectedRow?.recordType || requestType || '').toLowerCase().includes('address');
  const confidenceScore = firstPresentValue(
    agentSummary?.confidenceScore,
    agentSummary?.confidence_score,
    agentSummary?.confidence,
    selectedRow?.confidence_score,
    selectedRow?.confidenceScore,
    isAddressReq ? 75 : 91
  );
  const confidenceMeta = getConfidenceMeta(confidenceScore);
  const numericConfidence = confidenceMeta?.value || 0;
  const formattedNpi = firstPresentValue(
    hcpProfileSource?.npiId,
    hcpProfileSource?.npi,
    selectedRow?.npiId,
    selectedRow?.npi,
    account?.npiId,
    account?.npi,
    '-'
  );
  const formattedProf = firstPresentValue(
    hcpProfileSource?.primaryProfession,
    hcpProfileSource?.profession,
    selectedRow?.primaryProfession,
    selectedRow?.profession,
    account?.primaryProfession,
    account?.profession,
    '-'
  );
  const formattedSpec = firstPresentValue(
    hcpProfileSource?.primarySpecialty,
    hcpProfileSource?.specialty,
    selectedRow?.primarySpecialty,
    selectedRow?.specialty,
    account?.primarySpecialty,
    account?.specialty,
    '-'
  );
  const getSubSpecValueFromDetails = (details) => {
    if (!details) return null;
    if (Array.isArray(details)) {
      const first = details[0];
      return first?.spclDesc || first?.specialtyDesc || first?.specialty || first?.value || null;
    }
    if (typeof details === 'object') {
      const vals = Object.values(details);
      if (vals.length > 0) {
        const first = vals[0];
        return first?.spclDesc || first?.specialtyDesc || first?.specialty || first?.value || null;
      }
    }
    return null;
  };
  const formattedSubSpec = firstPresentValue(
    hcpProfileSource?.subSpecialty,
    hcpProfileSource?.subSpeciality,
    getSubSpecValueFromDetails(hcpProfileSource?.subSpecialtyDetails),
    selectedRow?.sub_speciality,
    selectedRow?.subSpecialty,
    account?.subSpecialty,
    getSubSpecValueFromDetails(account?.subSpecialtyDetails),
    '-'
  );
  const requestDateOnly = createdDateValue ? moment(createdDateValue).format('YYYY-MM-DD') : '-';
  const requestTimeOnly = createdDateValue ? moment(createdDateValue).format('HH:mm:ss') : '';
  const rawSla = selectedRow?.sla || selectedRow?.slaRemaining || agentSummary?.sla || '18h 42m';
  const timelineOverallStatus = normalizeOverallStatus(
    selectedRow?.detailedStatus ||
      selectedRow?.overallStatus ||
      statusDataResponse?.detailedStatus ||
      statusDataResponse?.overallStatus ||
      statusDataResponse?.status ||
      timelineDataResponse?.detailedStatus ||
      timelineDataResponse?.overallStatus ||
      timelineDataResponse?.status ||
      ''
  );

  // Extract custom changes from requestLine or from fieldName/oldValue/newValue arrays
  const parsedRequestLine = useMemo(() => {
    const changes = {}; // map of fieldName (lowercase) -> { oldValue, newValue }
    
    // 1. Parse requestLine if available
    const reqLine = selectedRow?.requestLine || selectedRow?.request_line;
    if (reqLine && typeof reqLine === 'object') {
      const lines = Array.isArray(reqLine) ? reqLine : Object.values(reqLine);
      lines.forEach((line) => {
        if (!line) return;
        const name = (line.fieldName || line.fieldApiName || '').toLowerCase();
        if (name) {
          changes[name] = {
            oldValue: line.oldValue,
            newValue: line.newValue,
          };
        }
      });
    }
    
    // 2. Parse fieldName/oldValue/newValue arrays if available (can override or supplement)
    const fieldNames = selectedRow?.fieldName || selectedRow?.field_name;
    const oldVals = selectedRow?.oldValue || selectedRow?.old_value;
    const newVals = selectedRow?.newValue || selectedRow?.new_value;
    
    if (Array.isArray(fieldNames)) {
      fieldNames.forEach((name, index) => {
        if (name && typeof name === 'string') {
          const lowerName = name.toLowerCase();
          const oldVal = Array.isArray(oldVals) ? oldVals[index] : undefined;
          const newVal = Array.isArray(newVals) ? newVals[index] : undefined;
          if (oldVal !== undefined || newVal !== undefined) {
            changes[lowerName] = {
              oldValue: oldVal !== undefined ? oldVal : (changes[lowerName]?.oldValue),
              newValue: newVal !== undefined ? newVal : (changes[lowerName]?.newValue),
            };
          }
        }
      });
    }
    
    return changes;
  }, [selectedRow]);

  const getDcrChange = (keys) => {
    for (const key of keys) {
      const lowerKey = key.toLowerCase();
      if (parsedRequestLine[lowerKey] !== undefined) {
        return parsedRequestLine[lowerKey];
      }
    }
    return null;
  };

  const addressLine1Change = getDcrChange(['addressline1', 'addressLine1', 'address_line_1', 'addrln1', 'addrLn1', 'addr_ln_1']);
  const addressLine2Change = getDcrChange(['addressline2', 'addressLine2', 'address_line_2', 'addrln2', 'addrLn2', 'addr_ln_2']);
  const cityChange = getDcrChange(['city', 'cityNm', 'city_name', 'citynm']);
  const stateChange = getDcrChange(['state', 'stcd', 'stdcd', 'st_cd']);
  const zipChange = getDcrChange(['zip', 'zipCode', 'zip_code', 'zipcd', 'zip_cd']);
  const countryChange = getDcrChange(['country', 'countryCd', 'country_code', 'country_cd']);
  const repChange = getDcrChange(['rep', 'repName', 'rep_name', 'repname', 'oldrep', 'newrep']);
  const territoryChange = getDcrChange(['territory', 'territoryName', 'territory_name', 'territoryname', 'oldterritory', 'newterritory']);
  const rankChange = getDcrChange(['rank', 'addrRank', 'addr_rank', 'rankNbr', 'rank_nbr']);

  const addressFields = [
    {
      label: 'Address Line 1',
      oldValue: addressLine1Change ? addressLine1Change.oldValue : firstPresentValue(agentSummary?.old_addressline1, agentSummary?.old_addrLn1, primaryAddress?.addrLn1, account?.addressLine),
      newValue: addressLine1Change ? addressLine1Change.newValue : firstPresentValue(agentSummary?.new_addressline1, agentSummary?.new_addrLn1, selectedRow?.new_address_line_1, account?.newAddressLine),
    },
    {
      label: 'Address Line 2',
      oldValue: addressLine2Change ? addressLine2Change.oldValue : firstPresentValue(agentSummary?.old_addressline2, agentSummary?.old_addrLn2, primaryAddress?.addrLn2, account?.addressLine2),
      newValue: addressLine2Change ? addressLine2Change.newValue : firstPresentValue(agentSummary?.new_addressline2, agentSummary?.new_addrLn2, selectedRow?.new_address_line_2, account?.newAddressLine2),
    },
    {
      label: 'City',
      oldValue: cityChange ? cityChange.oldValue : firstPresentValue(agentSummary?.old_cityNm, agentSummary?.old_city, primaryAddress?.cityNm, account?.city),
      newValue: cityChange ? cityChange.newValue : firstPresentValue(agentSummary?.new_cityNm, agentSummary?.new_city, selectedRow?.new_city, account?.newCity),
    },
    {
      label: 'State',
      oldValue: stateChange ? stateChange.oldValue : firstPresentValue(agentSummary?.old_stcd, agentSummary?.old_stdcd, agentSummary?.old_state, primaryAddress?.stCd, account?.state),
      newValue: stateChange ? stateChange.newValue : firstPresentValue(agentSummary?.new_stcd, agentSummary?.new_stdcd, agentSummary?.new_state, selectedRow?.new_state, account?.newState),
    },
    {
      label: 'Zip Code',
      oldValue: zipChange ? zipChange.oldValue : firstPresentValue(agentSummary?.old_zip, getZipPart(primaryAddress?.zipCd, 0), account?.zip),
      newValue: zipChange ? zipChange.newValue : firstPresentValue(agentSummary?.new_zip, selectedRow?.new_zip, account?.newZip),
    },
    {
      label: 'Country',
      oldValue: countryChange ? countryChange.oldValue : firstPresentValue(agentSummary?.old_country, agentSummary?.old_countryCd, account?.country, 'US'),
      newValue: countryChange ? countryChange.newValue : firstPresentValue(agentSummary?.new_country, agentSummary?.new_countryCd, account?.newCountry, 'US'),
    },
    {
      label: 'Rep',
      oldValue: repChange ? repChange.oldValue : firstPresentValue(agentSummary?.oldrep, agentSummary?.oldrepName, agentSummary?.old_rep, agentSummary?.old_repName, selectedRow?.old_rep),
      newValue: repChange ? repChange.newValue : firstPresentValue(agentSummary?.newrep, agentSummary?.newrepName, agentSummary?.new_rep, agentSummary?.new_repName, selectedRow?.new_rep),
    },
    {
      label: 'Territory',
      oldValue: territoryChange ? territoryChange.oldValue : firstPresentValue(agentSummary?.oldterritory, agentSummary?.oldterritoryName, agentSummary?.old_territory, agentSummary?.old_territoryName, selectedRow?.old_territory),
      newValue: territoryChange ? territoryChange.newValue : firstPresentValue(agentSummary?.newterritory, agentSummary?.newterritoryName, agentSummary?.new_territory, agentSummary?.new_territoryName, selectedRow?.new_territory),
    },
    {
      label: 'Rank',
      oldValue: rankChange ? rankChange.oldValue : firstPresentValue(agentSummary?.old_rank, agentSummary?.rank, primaryAddress?.rank, account?.rank),
      newValue: rankChange ? rankChange.newValue : firstPresentValue(agentSummary?.new_rank, agentSummary?.rank, selectedRow?.new_rank, account?.newRank),
    },
  ];

  const firstNameChange = getDcrChange(['firstName', 'first_name', 'firstname']);
  const middleNameChange = getDcrChange(['middleName', 'middle_name', 'middlename']);
  const lastNameChange = getDcrChange(['lastName', 'last_name', 'lastname']);
  const professionChange = getDcrChange(['profession', 'primaryProfession', 'primary_profession', 'primaryprofession']);
  const specialtyChange = getDcrChange(['specialty', 'speciality', 'primarySpecialty', 'primary_specialty', 'primaryspecialty']);
  const npiChange = getDcrChange(['npiId', 'npi_id', 'npiid', 'npi']);

  const accountFields = [
    {
      label: 'First Name',
      oldValue: firstNameChange ? firstNameChange.oldValue : firstPresentValue(agentSummary?.old_firstName, agentSummary?.old_first_name, agentSummary?.old_firstname, selectedRow?.old_firstName, selectedRow?.old_first_name, selectedRow?.old_firstname, hcpProfileSource?.firstName, hcpProfileSource?.fName, hcpProfileSource?.first_name, hcpProfileSource?.firstname),
      newValue: firstNameChange ? firstNameChange.newValue : firstPresentValue(agentSummary?.new_firstName, agentSummary?.new_first_name, agentSummary?.new_firstname, selectedRow?.new_firstName, selectedRow?.new_first_name, selectedRow?.new_firstname, hcpProfileSource?.firstName, hcpProfileSource?.fName, hcpProfileSource?.first_name, hcpProfileSource?.firstname),
    },
    {
      label: 'Middle Name',
      oldValue: middleNameChange ? middleNameChange.oldValue : firstPresentValue(agentSummary?.old_middleName, agentSummary?.old_middle_name, agentSummary?.old_middlename, selectedRow?.old_middleName, selectedRow?.old_middle_name, selectedRow?.old_middlename, hcpProfileSource?.middleName, hcpProfileSource?.mName, hcpProfileSource?.middle_name, hcpProfileSource?.middlename),
      newValue: middleNameChange ? middleNameChange.newValue : firstPresentValue(agentSummary?.new_middleName, agentSummary?.new_middle_name, agentSummary?.new_middlename, selectedRow?.new_middleName, selectedRow?.new_middle_name, selectedRow?.new_middlename, hcpProfileSource?.middleName, hcpProfileSource?.mName, hcpProfileSource?.middle_name, hcpProfileSource?.middlename),
    },
    {
      label: 'Last Name',
      oldValue: lastNameChange ? lastNameChange.oldValue : firstPresentValue(agentSummary?.old_lastName, agentSummary?.old_last_name, agentSummary?.old_lastname, selectedRow?.old_lastName, selectedRow?.old_last_name, selectedRow?.old_lastname, hcpProfileSource?.lastName, hcpProfileSource?.lName, hcpProfileSource?.last_name, hcpProfileSource?.lastname),
      newValue: lastNameChange ? lastNameChange.newValue : firstPresentValue(agentSummary?.new_lastName, agentSummary?.new_last_name, agentSummary?.new_lastname, selectedRow?.new_lastName, selectedRow?.new_last_name, selectedRow?.new_lastname, hcpProfileSource?.lastName, hcpProfileSource?.lName, hcpProfileSource?.last_name, hcpProfileSource?.lastname),
    },
    {
      label: 'Profession',
      oldValue: professionChange ? professionChange.oldValue : firstPresentValue(agentSummary?.old_primaryProfession, agentSummary?.old_primary_profession, selectedRow?.old_primaryProfession, selectedRow?.old_primary_profession, hcpProfileSource?.primaryProfession, hcpProfileSource?.profession),
      newValue: professionChange ? professionChange.newValue : firstPresentValue(agentSummary?.new_primaryProfession, agentSummary?.new_primary_profession, selectedRow?.new_primaryProfession, selectedRow?.new_primary_profession, hcpProfileSource?.primaryProfession, hcpProfileSource?.profession),
    },
    {
      label: 'Speciality',
      oldValue: specialtyChange ? specialtyChange.oldValue : firstPresentValue(agentSummary?.old_primarySpecialty, agentSummary?.old_primary_specialty, selectedRow?.old_primarySpecialty, selectedRow?.old_primary_specialty, hcpProfileSource?.primarySpecialty, hcpProfileSource?.specialty),
      newValue: specialtyChange ? specialtyChange.newValue : firstPresentValue(agentSummary?.new_primarySpecialty, agentSummary?.new_primary_specialty, selectedRow?.new_primarySpecialty, selectedRow?.new_primary_specialty, hcpProfileSource?.primarySpecialty, hcpProfileSource?.specialty),
    },
    {
      label: 'NPI ID',
      oldValue: npiChange ? npiChange.oldValue : firstPresentValue(agentSummary?.old_npiId, agentSummary?.old_npi_id, selectedRow?.old_npiId, selectedRow?.old_npi_id, hcpProfileSource?.npiId, hcpProfileSource?.npi),
      newValue: npiChange ? npiChange.newValue : firstPresentValue(agentSummary?.new_npiId, agentSummary?.new_npi_id, selectedRow?.new_npiId, selectedRow?.new_npi_id, hcpProfileSource?.npiId, hcpProfileSource?.npi),
    },
  ];

  const isAccountChange = (selectedRow?.record_type || selectedRow?.recordType || selectedRow?.request_type || selectedRow?.requestType || requestType || '').toLowerCase().includes('account');
  
  let evidenceItems = [];
  
  if (isAccountChange) {
    evidenceItems = [
      { label: 'Internet Search', desc: 'Provider located using Name + Address + Specialty', status: 'Match (98%)', passed: true },
      { label: 'NPI Verification', desc: 'Updated last name found in NPI Registry', status: 'Passed', passed: true },
      { label: 'Practice Address Match', desc: 'Address matches current master record', status: 'Match', passed: true },
      { label: 'Specialty Verification', desc: 'Specialty matches authoritative sources', status: 'Match', passed: true },
      { label: 'Confidence Score', desc: 'Overall AI confidence', status: '97%', passed: true },
    ];
  } else {
    evidenceItems = [
      { label: 'Address Standardization', desc: 'Address standardized using USPS rules', status: 'Passed', passed: true },
      { label: 'USPS Verification', desc: 'Address validated with USPS', status: 'Valid', passed: true },
      { label: 'Source Match', desc: 'Address found in authoritative sources', status: 'Match (100%)', passed: true },
      { label: 'Prescription Data', desc: 'No prescription volume available', status: 'Missing', passed: false },
      { label: 'NPI Validation', desc: 'NPI is active and valid', status: 'Passed', passed: true },
    ];
  }

  if (!selectedRow) {
    if (effectiveRequestId && loadingRequestDetails) {
      return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Paper
            elevation={0}
            sx={{
              minHeight: '72vh',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1.5,
              px: 3,
              textAlign: 'center',
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
              Loading request details...
            </Typography>
          </Paper>
        </Box>
      );
    }

    return (
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        <Paper
          elevation={0}
          sx={{
            minHeight: '72vh',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 1.5,
            px: 3,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#334155' }}>
            Request details not found. Please select a request from the history page.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/history/user')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 2.5,
              borderRadius: '8px',
              bgcolor: '#1E88E5',
              '&:hover': { bgcolor: '#1565C0' },
            }}
          >
            Back to History
          </Button>
        </Paper>
      </Box>
    );
  }

  const renderHistoryHeader = () => {
    const dividerElement = (
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          borderColor: '#e2e8f0',
          alignSelf: 'stretch',
          my: 0.5,
          display: { xs: 'none', lg: 'block' },
        }}
      />
    );

    const infoBlocks = [
      ['NPI', formattedNpi],
      ['Profession', formattedProf],
      ['Speciality', formattedSpec],
      ['Sub Speciality', formattedSubSpec],
    ];

    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
          borderLeft: '4.5px solid #0284c7',
          backgroundColor: '#fff',
          py: 1.5,
          px: 2.5,
          mb: 1.5,
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: { xs: 'wrap', lg: 'nowrap' },
            gap: { xs: 2, lg: 1 },
            width: '100%',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: { xs: '100%', lg: '24%' } }}>
            <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '1.25rem', fontFamily: '"Outfit", "Inter", sans-serif', lineHeight: 1.15 }}>
                DCR #{requestId || '-'}
              </Typography>
              <Chip size="small" label={requestTypeLabel} sx={{ height: 20, borderRadius: '4px', fontWeight: 700, fontSize: '0.68rem', color: '#2563eb', bgcolor: '#eff6ff', border: '1px solid #dbeafe' }} />
            </Stack>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.72rem', lineHeight: 1.2, display: 'block' }}>
                Managing data change request for:
              </Typography>
              <Typography variant="body2" sx={{ color: '#1E88E5', fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.25, display: 'block', mt: 0.25 }}>
                {hcpName} (MDM ID: {selectedMdmId || '-'})
              </Typography>
            </Box>
          </Box>

          {dividerElement}

          {infoBlocks.map(([label, value]) => (
            <React.Fragment key={label}>
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
                <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontSize: '0.8rem', textTransform: label.includes('Special') ? 'uppercase' : 'none', lineHeight: 1.2 }}>
                  {value}
                </Typography>
              </Box>
              {dividerElement}
            </React.Fragment>
          ))}

          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Status
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip 
                size="small" 
                label={
                  String(headerStatus).toLowerCase() === 'reject' ? 'Rejected' :
                  String(headerStatus).toLowerCase() === 'approve' ? 'Approved' :
                  formatTimelineKey(headerStatus)
                } 
                sx={{ 
                  height: 20, 
                  borderRadius: '4px', 
                  fontWeight: 700, 
                  fontSize: '0.68rem', 
                  color: ['APPROVED', 'APPROVE'].includes(String(headerStatus).toUpperCase()) ? '#16a34a' : ['REJECTED', 'REJECT'].includes(String(headerStatus).toUpperCase()) ? '#dc2626' : '#ea580c', 
                  bgcolor: ['APPROVED', 'APPROVE'].includes(String(headerStatus).toUpperCase()) ? '#dcfce7' : ['REJECTED', 'REJECT'].includes(String(headerStatus).toUpperCase()) ? '#fef2f2' : '#fff7ed', 
                  border: `1px solid ${['APPROVED', 'APPROVE'].includes(String(headerStatus).toUpperCase()) ? '#bbf7d0' : ['REJECTED', 'REJECT'].includes(String(headerStatus).toUpperCase()) ? '#fecaca' : '#ffedd5'}` 
                }} 
              />
            </Box>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.58rem', mt: 0.5, fontWeight: 500 }}>
              Since {requestDateDisplay}
            </Typography>
          </Box>

          {dividerElement}

          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' }, alignItems: 'flex-start' }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
              Confidence Score
            </Typography>
            {confidenceMeta ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress variant="determinate" value={confidenceMeta.value} size={22} thickness={4} sx={{ color: confidenceMeta.color }} />
                  <Box sx={{ inset: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CheckIcon sx={{ color: confidenceMeta.color, fontSize: 12, strokeWidth: 3 }} />
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>
                  {confidenceMeta.value}%
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>
                -
              </Typography>
            )}
          </Box>

          {dividerElement}

          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              SLA
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: '#ea580c', mt: 0.5, fontSize: '0.82rem' }}>
              {rawSla}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.58rem', fontWeight: 500, mt: 0.25 }}>
              Remaining
            </Typography>
          </Box>

          {dividerElement}

          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'calc(50% - 8px)', lg: 'auto' } }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800, fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Request Date
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', mt: 0.5, fontSize: '0.8rem', lineHeight: 1.2 }}>
              {requestDateOnly}
            </Typography>
            {requestTimeOnly ? (
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem', lineHeight: 1.2 }}>
                {requestTimeOnly}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderRequestInfoCard = () => (
    <Paper sx={cardStyle}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1.5, flexShrink: 0 }}>
        REQUEST INFO
      </Typography>
      <Grid container spacing={2} sx={{ flexGrow: 1, alignContent: 'space-between', mt: -2, '& > .MuiGrid-item': { pt: 2 } }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Record type</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{selectedRow?.record_type || 'Address'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Account name</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{account?.name || account?.account_name || '-'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Request ID</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{requestId || '-'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Request Date</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{formatTimestamp(selectedRow.created_date || selectedRow.created_at || selectedRow.createdAt) || '-'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>MDM ID</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{selectedMdmId || account?.mdm_id || '-'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Last modified</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{formatTimestamp(selectedRow.last_modified_date || selectedRow.modified_at || selectedRow.updated_at) || '-'}</Typography>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderRequestorCard = () => (
    <Paper sx={cardStyle}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1.5, flexShrink: 0 }}>
        REQUESTOR
      </Typography>
      <Grid container spacing={2} sx={{ flexGrow: 1, alignContent: 'space-between', mt: -2, '& > .MuiGrid-item': { pt: 2 } }}>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Name</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{requestor?.name || '-'}</Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Profile</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{requestor?.profile_name || requestor?.profileName || '-'}</Typography>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Email</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#1E88E5' }}>{requestor?.email || '-'}</Typography>
        </Grid>
        <Grid item xs={12} sm={12}>
          <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Employee no.</Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{requestor?.emp_number || requestor?.employeeId || '-'}</Typography>
        </Grid>
      </Grid>
    </Paper>
  );

  const renderHCPSummaryCard = () => {
    const fullName = [hcpProfileSource?.firstName || hcpProfileSource?.fName, hcpProfileSource?.middleName || hcpProfileSource?.mName, hcpProfileSource?.lastName || hcpProfileSource?.lName].filter(Boolean).join(' ');
    return (
      <Paper sx={cardStyle}>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1.5, flexShrink: 0 }}>
          HCP SUMMARY
        </Typography>
        <Grid container spacing={2} sx={{ flexGrow: 1, alignContent: 'space-between', mt: -2, '& > .MuiGrid-item': { pt: 2 } }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>MDM ID</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{selectedMdmId || account?.mdm_id || account?.mdmId || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>OneKey ID</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{hcpProfileSource?.okIndvId || hcpProfileSource?.onekeyId || account?.onekeyId || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Full name</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{fullName || account?.name || account?.account_name || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Profession</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{hcpProfileSource?.primaryProfession || hcpProfileSource?.profession || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>NPI</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{hcpProfileSource?.npiId || hcpProfileSource?.npi || '-'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>Status</Typography>
            <Chip size="small" label={hcpProfileSource?.statusLabel || hcpProfileSource?.status || hcpProfileSource?.activeStatus || '-'} sx={{ height: 20, bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 500,  }} />
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderChangesCard = () => {
    return( 
      <Paper sx={cardStyle}>
        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1.5, flexShrink: 0 }}>
          UPDATED DETAILS
        </Typography>
        {selectedFieldNames.length > 0 && 
         requestType?.toLowerCase() !== 'address' && 
         (selectedRow?.record_type || 'Address').toLowerCase() !== 'address' ? (
          <Box >
                <Grid container spacing={1.25}>
                          {selectedFieldNames.map((field, idx) => {
                            if (!field) return null;
                            const lowerField = field.toLowerCase();

                            let oldVal = '-';
                            let newVal = '-';
                            
                            const fieldChange = parsedRequestLine[lowerField];
                            if (fieldChange) {
                              oldVal = fieldChange.oldValue ?? '-';
                              newVal = fieldChange.newValue ?? '-';
                            } else {
                              const oldVals = selectedRow?.old_value || selectedRow?.oldValue;
                              if (Array.isArray(oldVals)) {
                                oldVal = oldVals[idx] ?? '-';
                              } else if (oldVals !== undefined && selectedFieldNames.length === 1) {
                                oldVal = oldVals;
                              } else if (selectedRow?.[`old_${field}`] !== undefined) {
                                oldVal = selectedRow[`old_${field}`];
                              } else if (statusData?.[0]?.[`old_${lowerField}`] !== undefined && statusData[0][`old_${lowerField}`] !== null) {
                                oldVal = statusData[0][`old_${lowerField}`];
                              }

                              const newVals = selectedRow?.new_value || selectedRow?.newValue;
                              if (Array.isArray(newVals)) {
                                newVal = newVals[idx] ?? '-';
                              } else if (newVals !== undefined && selectedFieldNames.length === 1) {
                                newVal = newVals;
                              } else if (selectedRow?.[`new_${field}`] !== undefined) {
                                newVal = selectedRow[`new_${field}`];
                              } else if (statusData?.[0]?.[`new_${lowerField}`] !== undefined && statusData[0][`new_${lowerField}`] !== null) {
                                newVal = statusData[0][`new_${lowerField}`];
                              }
                            }

                            const fieldLabelMap = {
                              firstname: 'First Name',
                              first_name: 'First Name',
                              middlename: 'Middle Name',
                              middle_name: 'Middle Name',
                              lastname: 'Last Name',
                              last_name: 'Last Name',
                              profession: 'Profession',
                              primaryprofession: 'Profession',
                              primary_profession: 'Profession',
                              specialty: 'Specialty',
                              speciality: 'Speciality',
                              primaryspecialty: 'Specialty',
                              primary_specialty: 'Specialty',
                              npiid: 'NPI ID',
                              npi_id: 'NPI ID',
                              npi: 'NPI ID'
                            };

                            const fieldLabel = fieldLabelMap[lowerField] || field;

                            return (
                              <Grid key={field} item xs={12} sm={12} md={12}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
                                  <Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        mb: 0.5,
                                      }}
                                    >
                                      Field changed
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ color: '#0f172a', fontWeight: 700,  }}>
                                      {fieldLabel}
                                    </Typography>
                                  </Box>
                                  
                                  <Divider sx={{ borderColor: '#f1f5f9' }} />

                                  <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#cbd5e1' }} />
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700,  textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                          OLD VALUE
                                        </Typography>
                                      </Stack>
                                      <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 500 }}>{oldVal}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22c55e' }} />
                                        <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 700,  textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                          NEW VALUE
                                        </Typography>
                                      </Stack>
                                      <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 500 }}>{newVal}</Typography>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, display: 'flex', minHeight: 100, alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              No updated fields available.
            </Typography>
          </Box>
        )}
      </Paper>
    );
  };

  const renderHCPFullDetailsCard = () => (
    <Paper sx={cardStyle}>
      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1.5 }}>
        HCP FULL DETAILS
      </Typography>
      {hcpProfileLoading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
          {['mdm-id', 'name', 'npi', 'specialty', 'address-line-1', 'city-state-zip'].map((skeletonKey) => (
            <Box key={`history-hcp-skeleton-${skeletonKey}`}>
              <Skeleton variant="text" width="60%" height={18} />
              <Skeleton variant="text" width="90%" height={22} />
            </Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
          {hcpDetailItems.map((item, idx) => {
            const isStatus = item.label === 'Status';
            const isActive = String(item.value || '').toLowerCase() === 'active' || String(item.value || '').toLowerCase() === 'a';
            return (
              <Box key={idx}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.25 }}>{item.label}</Typography>
                {isStatus ? (
                  <Chip size="small" label={item.value || '-'} sx={{ height: 20, bgcolor: isActive ? '#dcfce7' : '#f1f5f9', color: isActive ? '#16a34a' : '#475569', fontWeight: 600,  }} />
                ) : (
                  <Typography variant="body2" sx={{ fontWeight: 500, color: '#0f172a' }}>{item.value || '-'}</Typography>
                )}
              </Box>
            )
          })}
        </Box>
      )}
    </Paper>
  );

  const renderSourceRecordsTable = () => (
    <Paper sx={{ ...cardStyle, p: 0, overflow: 'hidden' }}>
      <GlobalMaterialTable
        columns={activeSourceRecordTab.columns.map((column) => ({
          header: column.label,
          id: `${activeSourceRecordTab.keyPrefix}-${column.label}`,
          accessorFn: (row) => displayValue(column.render ? column.render(row) : getTabValue(row, column.keys)),
        }))}
        data={activeSourceRecordTab.rows}
        emptyMessage={activeSourceRecordTab.emptyMessage}
        enablePagination
        enableTopToolbar
        enableBottomToolbar
        initialState={{
          density: 'compact',
        }}
        state={{
          pagination
        }}
        onPaginationChange={setPagination}
        muiTablePaginationProps={
          {rowsPerPageOptions: [3, 5, 10, 20, 50, 100],}
        }
        pageSize={10}
        autoResetPageIndex={false}
        rowCount={activeSourceRecordTab.rows.length}
        manualPagination={false}
        renderTopToolbar={() => (
          <Box
            sx={{
              minHeight: 48,
              display: 'flex',
              alignItems: 'center',
              borderBottom: '1px solid #e2e8f0',
              bgcolor: '#fff',
            }}
          >
            <Tabs
              value={sourceRecordsTabIndex}
              onChange={(_, nextValue) => setSourceRecordsTabIndex(nextValue)}
              sx={{
                minHeight: 40,
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  bgcolor: '#1E88E5',
                },
              }}
            >
              {sourceRecordTabConfigs.map((tab, index) => (
                <Tab
                  key={tab.label}
                  label={tab.label}
                  sx={{
                    minHeight: 40,
                    px: 2,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 600,
                    color: '#64748b',
                    '&.Mui-selected': { color: '#1E88E5' },
                  }}
                  value={index}
                />
              ))}
            </Tabs>
          </Box>
        )}
        getRowId={(row, index) =>
          `${activeSourceRecordTab.keyPrefix}-${row.addrId || row.srname || row.slnAuthNbr || row.Id || row.rank || index}`
        }
        muiTablePaperProps={{ elevation: 0, sx: { borderRadius: '8px', border: '1px solid #e2e8f0', boxShadow: 'none' } }}
        muiTableContainerProps={{ sx: { maxHeight: 520, overflowX: 'auto' } }}
        muiTableProps={{ sx: { tableLayout: 'auto' } }}
        muiTableBodyRowProps={({ row }) =>
          isMasterPrimaryAddress(row.original)
            ? {
                sx: {
                  '& td': { backgroundColor: alpha('#f59e0b', 0.12) },
                  '&:hover td': { backgroundColor: alpha('#f59e0b', 0.18) },
                },
              }
            : {}
        }
      />
    </Paper>
  );

  const isPageLoading = loadingStatus || loadingTimeline || hcpProfileLoading || loadingRequestDetails || loadingCards;

  if (isPageLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 100px)', p: { xs: 1, sm: 2, md: 3 } }}>
        <Skeleton variant="text" width={200} height={24} sx={{ mb: 2 }} />
        <Paper sx={{ p: 3, mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Box>
              <Skeleton variant="text" width={300} height={40} />
              <Skeleton variant="text" width={200} height={20} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="rounded" width={100} height={40} />
              <Skeleton variant="rounded" width={100} height={40} />
            </Box>
          </Box>
          <Grid container spacing={2}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={2.4} key={i}>
                <Skeleton variant="text" width="60%" height={20} />
                <Skeleton variant="text" width="90%" height={24} />
              </Grid>
            ))}
          </Grid>
        </Paper>
        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: '12px', mb: 2 }} />
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: '12px' }} />
          </Grid>
          <Grid item xs={12} lg={4}>
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px', mb: 2 }} />
            <Skeleton variant="rectangular" height={250} sx={{ borderRadius: '12px', mb: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '12px' }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 100px)' }}>
      <Box sx={{ flex: 1, pb: 2 }}>
        <Box sx={{ mb: 1 }}>
          <PageBreadcrumbs
            items={[
              { label: 'DCR History', path: '/history/user' },
              { label: 'DCR Request' },
            ]}
          />
        </Box>
        {renderHistoryHeader()}

        <Grid container spacing={2}>
          <Grid item xs={12} lg={8}>
            <Stack spacing={2}>
              <Paper sx={{ ...cardStyle, p: 2.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1E88E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>CHANGE COMPARISON</Typography>
                <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', fontWeight: 500 }}>Review the current and requested changes</Typography>
                {isAccountChange && (
                  <>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block', mt:1 }}>Account Information</Typography>
                    <Grid container spacing={1.5} alignItems="stretch" sx={{ mb: 1.5 }}>
                      {[['CURRENT ACCOUNT INFO', '#3b82f6', accountFields.map((field) => ({ ...field, value: field.oldValue }))], ['REQUESTED ACCOUNT INFO', '#22c55e', accountFields.map((field) => ({ ...field, value: field.newValue }))]].map(([title, color, rows], index) => (
                        <React.Fragment key={title}>
                          <Grid item xs={12} md={5.5}>
                            <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0', borderRadius: '8px', height: '100%' }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                {index === 0 ? <MonitorHeartRoundedIcon sx={{ color, fontSize: 18 }} /> : <CheckCircleRoundedIcon sx={{ color, fontSize: 18 }} />}
                                <Typography variant="caption" sx={{ fontWeight: 800, color, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{title}</Typography>
                              </Stack>
                              <Stack spacing={1}>
                                {rows.map((field) => (
                                  <Box key={field.label} sx={{ display: 'flex', minHeight: 20, justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f8fafc' }}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>{field.label}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: index === 1 ? '#15803d' : '#1e293b', fontSize: '0.78rem' }}>{displayValue(field.value)}</Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Paper>
                          </Grid>
                          {index === 0 && (
                            <Grid item xs={12} md={1} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                              {accountFields.map((field) => <ArrowForwardRoundedIcon key={field.label} sx={{ color: '#cbd5e1', my: 0.5 }} />)}
                            </Grid>
                          )}
                        </React.Fragment>
                      ))}
                    </Grid>
                  </>
                )}

                {!isAccountChange && (
                  <>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'block'}}>Address Details</Typography>
                    <Grid container spacing={1.5} alignItems="stretch">
                      {[['CURRENT ADDRESS', '#3b82f6', addressFields.map((field) => ({ ...field, value: field.oldValue }))], ['REQUESTED ADDRESS', '#22c55e', addressFields.map((field) => ({ ...field, value: field.newValue }))]].map(([title, color, rows], index) => (
                        <React.Fragment key={title}>
                          <Grid item xs={12} md={5.5}>
                            <Paper variant="outlined" sx={{ p: 2, borderColor: '#e2e8f0', borderRadius: '8px', height: '100%' }}>
                              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                                {index === 0 ? <MonitorHeartRoundedIcon sx={{ color, fontSize: 18 }} /> : <CheckCircleRoundedIcon sx={{ color, fontSize: 18 }} />}
                                <Typography variant="caption" sx={{ fontWeight: 800, color, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{title}</Typography>
                              </Stack>
                              <Stack spacing={1}>
                                {rows.map((field) => {
                                  const changed = displayValue(field.oldValue) !== displayValue(field.newValue);
                                  const chipColor = index === 1 ? '#15803d' : '#b91c1c';
                                  const chipBg = index === 1 ? '#dcfce7' : '#fee2e2';
                                  const useChip = changed && ['State', 'Zip Code'].includes(field.label);
                                  return (
                                    <Box key={field.label} sx={{ display: 'flex', minHeight: 20, justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f8fafc' }}>
                                      <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>{field.label}</Typography>
                                      {useChip ? <Chip size="small" label={displayValue(field.value)} sx={{ height: 20, borderRadius: '4px', fontWeight: 700, fontSize: '0.7rem', color: chipColor, bgcolor: chipBg }} /> : <Typography variant="body2" sx={{ fontWeight: 700, color: index === 1 && changed ? '#15803d' : '#1e293b', fontSize: '0.78rem' }}>{displayValue(field.value)}</Typography>}
                                    </Box>
                                  );
                                })}
                              </Stack>
                            </Paper>
                          </Grid>
                          {index === 0 && (
                            <Grid item xs={12} md={1} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                              {addressFields.map((field) => <ArrowForwardRoundedIcon key={field.label} sx={{ color: '#cbd5e1', my: 0.45 }} />)}
                            </Grid>
                          )}
                        </React.Fragment>
                      ))}
                    </Grid>
                  </>
                )}
              </Paper>

              {renderSourceRecordsTable()}
            </Stack>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Stack spacing={2}>
              {cardsDetails?.aiRecommendation && (
                <Paper sx={cardStyle}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <MonitorHeartRoundedIcon sx={{ color: '#1E88E5', fontSize: 16 }} />
                      <Typography variant="caption" sx={{ fontWeight: 800, color: '#1E88E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>AI Recommendation</Typography>
                    </Stack>
                  </Box>
                  {/* <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', textTransform: 'uppercase', mb: 1 }}>Recommendation</Typography> */}
                  <Box sx={{ p: 1.25, mb: 2, 
                      bgcolor: cardsDetails.aiRecommendation.recommendation.toLowerCase().includes('reject') ? '#fee2e2' : (cardsDetails.aiRecommendation.status === 'human_review_required' ? '#ffedd5' : '#dcfce7'), 
                      borderLeft: `4px solid ${cardsDetails.aiRecommendation.recommendation.toLowerCase().includes('reject') ? '#dc2626' : (cardsDetails.aiRecommendation.status === 'human_review_required' ? '#f97316' : '#16a34a')}`, 
                      borderRadius: '4px', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Avatar sx={{ width: 20, height: 20, bgcolor: alpha(cardsDetails.aiRecommendation.recommendation.toLowerCase().includes('reject') ? '#dc2626' : (cardsDetails.aiRecommendation.status === 'human_review_required' ? '#f97316' : '#16a34a'), 0.16), color: cardsDetails.aiRecommendation.recommendation.toLowerCase().includes('reject') ? '#dc2626' : (cardsDetails.aiRecommendation.status === 'human_review_required' ? '#f97316' : '#16a34a') }}><PersonRoundedIcon sx={{ fontSize: 14 }} /></Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: cardsDetails.aiRecommendation.recommendation.toLowerCase().includes('reject') ? '#dc2626' : (cardsDetails.aiRecommendation.status === 'human_review_required' ? '#f97316' : '#16a34a'), fontSize: '0.78rem' }}>
                      {cardsDetails.aiRecommendation.recommendation === 'human_review_required' ? 'Human review required' : cardsDetails.aiRecommendation.recommendation}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', textTransform: 'uppercase', mb: 1 }}>Reasoning</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: '0.75rem', color: '#475569', maxHeight: 90, overflowY: 'auto'}}>
                    {cardsDetails.aiRecommendation.reasoning?.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </Box>
                </Paper>
              )}

              {cardsDetails?.evidenceSummary && (
                <Paper sx={cardStyle}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#1E88E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>EVIDENCE SUMMARY</Typography>
                  </Box>
                  <Stack spacing={1.5}>
                    {cardsDetails.evidenceSummary.map((item) => (
                      <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f8fafc', gap: 1 }}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.78rem', lineHeight: 1.2 }}>{item.label}</Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.68rem' }}>{item.desc || item.description}</Typography>
                        </Box>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {item.passed ? <CheckIcon sx={{ color: '#22c55e', fontSize: 13 }} /> : <CloseIcon sx={{ color: '#ef4444', fontSize: 13 }} />}
                          <Typography variant="caption" sx={{ fontWeight: 700, color: item.passed ? '#22c55e' : '#ef4444', fontSize: '0.7rem' }}>{item.status}</Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              )}

              {cardsDetails?.requesterInformation && (
                <Paper sx={cardStyle}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#1E88E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>REQUESTER INFORMATION</Typography>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: '#eff6ff', color: '#1E88E5' }}><PersonRoundedIcon sx={{ fontSize: 16 }} /></Avatar>
                  </Box>
                  <Grid container spacing={2} sx={{maxHeight: activeSourceRecordTab?.rows?.length >= 1 ? "auto" : 90, overflow:"auto"}}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>Requested By</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.8rem' }}>{cardsDetails.requesterInformation.requestedBy}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>Profile</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.8rem' }}>{cardsDetails.requesterInformation.profile}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>Email</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#2563eb', fontSize: '0.8rem', overflowWrap: 'anywhere' }}>{cardsDetails.requesterInformation.email}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>Request Date</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.8rem' }}>{cardsDetails.requesterInformation.requestDate}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {cardsDetails?.regionalManager && (
                <Paper sx={cardStyle}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#1E88E5', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                      REGIONAL MANAGER
                    </Typography>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: '#eff6ff', color: '#1E88E5', fontSize: '0.75rem' }}>
                      <PersonRoundedIcon sx={{ fontSize: 16 }} />
                    </Avatar>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                    <Avatar sx={{ width: 40, height: 40, bgcolor: '#f1f5f9', color: '#64748b' }}>
                      <PersonRoundedIcon />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                        {cardsDetails.regionalManager.name}
                      </Typography>
                      <Chip label={cardsDetails.regionalManager.region} size="small" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#e0e7ff', color: '#4338ca', mt: 0.5 }} />
                      {cardsDetails.regionalManager.email && (
                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mt: 0.5 }}>
                          {cardsDetails.regionalManager.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              )}
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ ...cardStyle, p: 0, overflow: 'hidden' }}>
              <WorkflowTimelineBottomBar
                workflowSteps={workflowSteps}
                timelineUsesMockData={false}
                agentOverallStatus={timelineOverallStatus}
                isAccountChange={isAccountChange}
              />
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* <Paper elevation={4} sx={{ position: 'sticky', bottom: 0, zIndex: 1000, bgcolor: '#fff', borderTop: '1px solid #e2e8f0', py: 1.5, px: 3, mx: -2, mb: -3, mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.05)' }}>
        <Button startIcon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />} sx={{ color: '#64748b', textTransform: 'none', fontWeight: 700, fontSize: '0.85rem' }}>
          Add Internal Note
        </Button>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<CancelOutlinedIcon sx={{ fontSize: 16 }} />} sx={{ borderColor: '#fca5a5', color: '#dc2626', textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', borderRadius: '6px', px: 2.5, py: 0.75 }}>Reject</Button>
          <Button variant="outlined" startIcon={<CallMadeIcon sx={{ fontSize: 16 }} />} sx={{ borderColor: '#cbd5e1', color: '#2563eb', textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', borderRadius: '6px', px: 2.5, py: 0.75 }}>Escalate</Button>
          <Button variant="contained" startIcon={<CheckIcon sx={{ fontSize: 16 }} />} sx={{ bgcolor: '#10b981', color: '#fff', textTransform: 'none', fontWeight: 700, fontSize: '0.82rem', borderRadius: '6px', px: 2.5, py: 0.75, boxShadow: 'none', '&:hover': { bgcolor: '#059669', boxShadow: 'none' } }}>Approve</Button>
        </Stack>
      </Paper> */}
    </Box>
  );
};

export default HistoryRequestDetails;
